import React from 'react';
import { Box, Typography, Card, CardContent, Container, Avatar, Grid } from '@mui/material';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

// Dynamically import images from the folder
const importImages = (requireContext) => {
    return requireContext.keys().map(requireContext);
};

// Import all images in the specified folder (replace with your folder path)
const images = importImages(require.context('../Images/Carousel', false, /\.(jpg|jpeg|png|mov)$/));
const avatars = importImages(require.context('../Images/Avatars', false, /\.(jpg|jpeg|png)$/));

const About = () => {
    return (
        <Container sx={{ py: 15 }}>
            {/* Philosophy Section */}
            <Card sx={{ mb: 4, p: 2 }}>
                <CardContent>
                    <Typography variant="h4" gutterBottom>
                        Great Lakes Golf Philosophy
                    </Typography>

                    <Typography variant="body1" paragraph>
                        At Great Lakes Golf Academy, we recognize that success on the course is a balance of technical skill, tactical strategy, functional fitness, and mental fortitude. This balance will look different for every student, but our teaching philosophy, with a primary focus on education, remains the same.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        When golfers are able to understand the fundamentals of the game, it allows them to self-diagnose and fix their own mistakes. Our role is to educate in all areas of the game and implement a personal plan that will build on the strengths of each individual while simultaneously eliminating their weaknesses. Our instructors focus on building a strong foundation of fundamentals, which then allows each individual to refine their technical skills in their own unique way.
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Beyond mechanics, we cultivate a deep understanding of course management, honing decision-making skills crucial to navigating the complexities of golf. Through a supportive and collaborative learning environment, we encourage camaraderie and growth among our students. The Great Lakes Golf Academy total package approach will give you the tools to take complete control of your game and achieve your goals.
                    </Typography>
                </CardContent>
            </Card>

            {/* Meet Our Instructors Section */}
            <Card sx={{ mb: 4, p: 2 }}>
                <CardContent>
                    <Typography variant="h4" gutterBottom>
                        Meet Our Instructors
                    </Typography>

                    <Grid container spacing={4}>
                        {/* JC Burgess */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    src={avatars[1]}  // Assuming JC's avatar is the first image in the folder
                                    alt="JC Burgess"
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        mr: 2,
                                        alignSelf: 'flex-start', // Ensuring alignment to the top
                                    }}
                                />
                                <Box>
                                    <Typography variant="h5">JC Burgess</Typography>
                                    <Typography variant="body1" paragraph>
                                        JC Burgess grew up in London, Ontario, and was introduced to the game of golf by his dad at a young age. As a kid, he spent most days in the summer playing 36 holes at the local city courses. He started playing on the local junior tours, eventually graduating to tournaments on the provincial and national level. JC attended an elite junior academy in Michigan, learning under PGA award-winning coaches Brad Dean and Scott Wilson. It was during his teenage years in Michigan that JC began to understand the intricacies and technical and tactical aspects of golf at an elite level.
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                        He earned a scholarship and played Division I golf at the University of Louisiana. During the summer, he would caddie at Arcadia Bluffs, a top 100 course in America, where he would help golfers of all skill levels navigate the course and provide tips to improve their game. After graduating from UL, JC attended Western University, where he earned a Bachelor of Education Degree. He also played varsity golf and earned OUA All-Star and OUA All-Academic honors. JC is currently a high school Physical Education teacher. His experience as an educator and his success in competitive golf are the tools he uses to build his students up and help them get the most out of their game.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Chase Komaromi */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                    src={avatars[0]}  // Assuming Chase's avatar is the second image in the folder
                                    alt="Chase Komaromi"
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        mr: 2,
                                        alignSelf: 'flex-start', // Ensuring alignment to the top
                                    }}
                                />
                                <Box>
                                    <Typography variant="h5">Chase Komaromi</Typography>
                                    <Typography variant="body1" paragraph>
                                        Chase Komaromi played many sports growing up but gravitated towards golf around the age of 12. Joining The Oaks Golf Club kickstarted his golf journey. He spent endless nights after school playing and practicing, which prepared him to start playing in tournaments. Chase had success as a competitive junior golfer, winning a few local junior events and qualifying for a Canadian Junior Championship in Nova Scotia. His early success helped grow his passion for the game.
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                        In high school, Chase started to elevate his game, competing in more prominent amateur events. Through elite-level instruction and commitment, he was able to win numerous amateur tournaments, including the Ontario Men’s Match Play Championship in 2017. After winning his club championship two seasons in a row and finishing his last amateur season ranked inside the top 15 amateur golfers in the province, he decided to turn pro and test his game at the next level. Chase is currently playing professionally and has played on several mini-tours locally and in the United States, and has also competed in numerous PGA Tour Canada events. With all the experience and lessons learned on his journey so far, he’s excited to help others improve their games and push them towards whatever level they aspire to achieve.
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Image Carousel Section */}
            <Card sx={{ mb: 4, p: 2 }}>
                <Box sx={{ marginBottom: 4, marginTop: 4 }}>
                    <Carousel
                        showThumbs={false}
                        infiniteLoop={true}
                        autoPlay={true}
                        showStatus={false}
                        renderArrowPrev={(onClickHandler, hasPrev) => (
                            hasPrev && (
                                <button
                                    onClick={onClickHandler}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '10px',
                                        transform: 'translateY(-50%)',
                                        background: 'rgba(0, 0, 0, 0.7)', // Dark background
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px', // Rounded corners, not fully circular
                                        padding: '8px 12px', // Balanced padding
                                        zIndex: 1000,
                                        fontSize: '18px', // Appropriate font size for the arrow
                                        cursor: 'pointer', // Make it appear clickable
                                        transition: 'background-color 0.3s ease, transform 0.2s ease', // Smooth transition for hover
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(0, 0, 0, 0.9)'; // Darken on hover
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(0, 0, 0, 0.7)'; // Reset background color
                                    }}
                                >
                                    &#10094;
                                </button>
                            )
                        )}
                        renderArrowNext={(onClickHandler, hasNext) => (
                            hasNext && (
                                <button
                                    onClick={onClickHandler}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        background: 'rgba(0, 0, 0, 0.7)', // Dark background
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px', // Rounded corners, not fully circular
                                        padding: '8px 12px', // More balanced padding
                                        zIndex: 1000,
                                        fontSize: '18px', // Make the arrow a reasonable size
                                        cursor: 'pointer', // Change cursor to pointer on hover
                                        transition: 'background-color 0.3s ease, transform 0.2s ease', // Smooth transition for hover
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(0, 0, 0, 0.9)'; // Darken on hover
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(0, 0, 0, 0.7)'; // Reset the background color
                                    }}
                                >
                                    &#10095;
                                </button>
                            )
                        )}
                    >
                        {images.map((image, index) => (
                            <div key={index}>
                                <img
                                    src={image}
                                    alt={`carousel-image-${index}`}
                                    style={{
                                        width: '100%',
                                        maxHeight: '400px',
                                        objectFit: 'contain',
                                    }}
                                />
                            </div>
                        ))}
                    </Carousel>
                </Box>
            </Card>

        </Container>
    );
};

export default About;
